import { useTranslation } from 'react-i18next';

import { Pagination } from '@/components/data/Pagination';

import { usePatientInquiriesQuery } from '../hooks/use-patient-inquiries';
import { usePatientInquiryListStore } from '../store/patient-inquiry-list.store';
import {
  PatientInquiryListEmpty,
  PatientInquiryListSkeleton,
  PatientInquiryListTable,
} from './PatientInquiryListTable';

interface PatientInquiryListViewProps {
  // Slice 4 wires the slide panel here. Until then, row clicks are no-ops.
  onRowClick?: (id: number) => void;
}

function PatientInquiryListError({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('admin');
  return (
    <div
      role="alert"
      style={{
        padding: 16,
        border: '1px solid var(--destructive)',
        borderRadius: 8,
        background: '#FEE2E8',
        color: 'var(--destructive)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
      }}
    >
      <span>{t('patientInquiry.error.list')}</span>
      <button type="button" className="btn btn-sm btn-outline" onClick={onRetry}>
        {t('patientInquiry.actions.retry')}
      </button>
    </div>
  );
}

export function PatientInquiryListView({ onRowClick }: PatientInquiryListViewProps) {
  const search = usePatientInquiryListStore((s) => s.filters.search);
  const sort = usePatientInquiryListStore((s) => s.sort);
  const page = usePatientInquiryListStore((s) => s.page);
  const perPage = usePatientInquiryListStore((s) => s.perPage);
  const setPage = usePatientInquiryListStore((s) => s.setPage);

  // Empty keyword is omitted from the wire so the service layer treats it as
  // "no filter" rather than an empty-string match.
  const params = {
    keyword: search.trim() || undefined,
    page,
    perPage,
    sortBy: sort.key,
    sortDir: sort.dir,
  };

  const { data, isLoading, error, refetch } = usePatientInquiriesQuery(params);

  const isFiltered = Boolean(params.keyword);

  if (isLoading) return <PatientInquiryListSkeleton />;
  if (error) return <PatientInquiryListError onRetry={() => void refetch()} />;
  if (!data || data.items.length === 0)
    return <PatientInquiryListEmpty filtered={isFiltered} />;

  const totalPages = Math.max(1, Math.ceil(data.total / data.perPage));

  return (
    <>
      <PatientInquiryListTable items={data.items} onRowClick={onRowClick} />
      <Pagination page={data.page} totalPages={totalPages} onPageChange={setPage} />
    </>
  );
}
